import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api from '../utils/api';
import {
  Users, Briefcase, Clock, Mail, Phone, Linkedin, Github,
  FileText, Download, Trash2, MessageSquare, ChevronDown,
  ChevronUp, Filter, RefreshCw, CheckCircle, XCircle,
  Eye, Loader2, BarChart3, User, Calendar, Lock, ShieldCheck,
  AlertCircle
} from 'lucide-react';

const CareersAdmin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterJob, setFilterJob] = useState('all');
  const [noteInput, setNoteInput] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Check if user is logged in and has admin role
  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setCheckingAuth(false);
      setIsAuthenticated(false);
      return;
    }

    try {
      const response = await api.get('/careers/admin/check');
      setIsAuthenticated(true);
      setIsAdmin(response.data.is_admin);
      if (response.data.is_admin) {
        fetchData();
      }
    } catch (error) {
      console.error('Admin check failed:', error);
      setIsAuthenticated(false);
      setIsAdmin(false);
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleLogin = () => {
    // Redirect to login page with return URL
    navigate('/login?redirect=/careers/admin');
  };

  const statusColors = {
    new: 'bg-blue-100 text-blue-800',
    reviewing: 'bg-yellow-100 text-yellow-800',
    interviewed: 'bg-purple-100 text-purple-800',
    offered: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    withdrawn: 'bg-gray-100 text-gray-800'
  };

  const statusOptions = ['new', 'reviewing', 'interviewed', 'offered', 'rejected', 'withdrawn'];

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [filterStatus, filterJob, isAdmin]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterJob !== 'all') params.append('job_id', filterJob === 'open' ? '' : filterJob);
      
      const [appsRes, jobsRes, statsRes] = await Promise.all([
        api.get(`/careers/applications?${params.toString()}`),
        api.get('/careers/jobs'),
        api.get('/careers/stats')
      ]);
      
      setApplications(appsRes.data);
      setJobs(jobsRes.data.jobs);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 403) {
        toast({ title: "Access Denied", description: "You don't have admin privileges", variant: "destructive" });
        setIsAdmin(false);
      } else {
        toast({ title: "Error", description: "Failed to load applications", variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (appId, newStatus) => {
    try {
      await api.put(`/careers/applications/${appId}/status?status=${newStatus}`);
      toast({ title: "Success", description: `Status updated to ${newStatus}` });
      fetchData();
      if (selectedApp && selectedApp._id === appId) {
        setSelectedApp({ ...selectedApp, status: newStatus });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const addNote = async (appId) => {
    if (!noteInput.trim()) return;
    
    try {
      setAddingNote(true);
      await api.post(`/careers/applications/${appId}/notes?note=${encodeURIComponent(noteInput)}`);
      toast({ title: "Success", description: "Note added" });
      setNoteInput('');
      
      // Refresh the selected application
      const res = await api.get(`/careers/applications/${appId}`);
      setSelectedApp(res.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to add note", variant: "destructive" });
    } finally {
      setAddingNote(false);
    }
  };

  const deleteApplication = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    
    try {
      await api.delete(`/careers/applications/${appId}`);
      toast({ title: "Success", description: "Application deleted" });
      setSelectedApp(null);
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete application", variant: "destructive" });
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Loading state
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  // Not logged in
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">Admin Access Required</CardTitle>
            <p className="text-gray-500 text-sm mt-2">Please log in with an admin account to continue</p>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleLogin} 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="admin-login-btn"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              Log In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Logged in but not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl">Access Denied</CardTitle>
            <p className="text-gray-500 text-sm mt-2">
              Your account doesn't have admin privileges. Contact the system administrator if you believe this is an error.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              onClick={() => navigate('/dashboard')} 
              className="w-full"
              variant="outline"
            >
              Go to Dashboard
            </Button>
            <Button 
              onClick={() => navigate('/careers')} 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              View Careers Page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && !applications.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="careers-admin-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2" data-testid="admin-title">Careers Admin</h1>
            <p className="text-gray-300">Manage job applications and track candidates</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-500">Admin</Badge>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <Card className="bg-white" data-testid="stat-total">
              <CardContent className="pt-4 text-center">
                <BarChart3 className="h-6 w-6 mx-auto text-gray-400 mb-2" />
                <p className="text-2xl font-bold">{stats.total_applications}</p>
                <p className="text-xs text-gray-500">Total</p>
              </CardContent>
            </Card>
            {statusOptions.map(status => (
              <Card key={status} className="bg-white" data-testid={`stat-${status}`}>
                <CardContent className="pt-4 text-center">
                  <Badge className={`${statusColors[status]} mb-2`}>{status}</Badge>
                  <p className="text-xl font-bold">{stats.by_status[status] || 0}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {statusOptions.map(s => (
                    <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterJob} onValueChange={setFilterJob}>
                <SelectTrigger className="w-48" data-testid="filter-job">
                  <SelectValue placeholder="Position" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Positions</SelectItem>
                  <SelectItem value="open">Open Application</SelectItem>
                  {jobs.map(j => (
                    <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Applications List */}
          <div className="lg:col-span-1 space-y-3">
            <h2 className="font-semibold text-gray-700 mb-3">
              Applications ({applications.length})
            </h2>
            
            {applications.length === 0 ? (
              <Card className="text-center py-8">
                <p className="text-gray-500">No applications found</p>
              </Card>
            ) : (
              applications.map(app => (
                <Card 
                  key={app._id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedApp?._id === app._id ? 'ring-2 ring-emerald-500' : ''
                  }`}
                  onClick={() => setSelectedApp(app)}
                  data-testid={`app-card-${app._id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold">{app.full_name}</h3>
                      <Badge className={statusColors[app.status]}>{app.status}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{app.job_title || 'Open Application'}</p>
                    <p className="text-xs text-gray-400">{formatDate(app.created_at)}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Application Detail */}
          <div className="lg:col-span-2">
            {selectedApp ? (
              <Card data-testid="app-detail">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <User className="h-5 w-5" />
                        {selectedApp.full_name}
                      </CardTitle>
                      <p className="text-gray-500 mt-1">{selectedApp.job_title || 'Open Application'}</p>
                    </div>
                    <div className="flex gap-2">
                      <Select 
                        value={selectedApp.status} 
                        onValueChange={(val) => updateStatus(selectedApp._id, val)}
                      >
                        <SelectTrigger className="w-36" data-testid="status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map(s => (
                            <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        variant="destructive" 
                        size="icon"
                        onClick={() => deleteApplication(selectedApp._id)}
                        data-testid="delete-btn"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Contact Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <a href={`mailto:${selectedApp.email}`} className="text-emerald-600 hover:underline">
                        {selectedApp.email}
                      </a>
                    </div>
                    {selectedApp.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span>{selectedApp.phone}</span>
                      </div>
                    )}
                    {selectedApp.linkedin && (
                      <div className="flex items-center gap-2 text-sm">
                        <Linkedin className="h-4 w-4 text-gray-400" />
                        <a href={selectedApp.linkedin.startsWith('http') ? selectedApp.linkedin : `https://${selectedApp.linkedin}`} 
                           target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
                          LinkedIn
                        </a>
                      </div>
                    )}
                    {selectedApp.portfolio && (
                      <div className="flex items-center gap-2 text-sm">
                        <Github className="h-4 w-4 text-gray-400" />
                        <a href={selectedApp.portfolio.startsWith('http') ? selectedApp.portfolio : `https://${selectedApp.portfolio}`} 
                           target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
                          Portfolio
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Experience & Skills */}
                  <div>
                    <h4 className="font-medium mb-2">Experience & Skills</h4>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Badge variant="outline">{selectedApp.experience_years} years exp</Badge>
                      {selectedApp.current_role && (
                        <Badge variant="outline">{selectedApp.current_role}</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedApp.skills?.map((skill, i) => (
                        <Badge key={i} className="bg-emerald-100 text-emerald-800">{skill}</Badge>
                      ))}
                    </div>
                  </div>

                  {/* Why Wayo */}
                  <div>
                    <h4 className="font-medium mb-2">Why Wayo?</h4>
                    <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                      {selectedApp.why_wayo}
                    </p>
                  </div>

                  {/* How Contribute */}
                  <div>
                    <h4 className="font-medium mb-2">How They Can Contribute</h4>
                    <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                      {selectedApp.how_contribute}
                    </p>
                  </div>

                  {/* Additional Info */}
                  {selectedApp.additional_info && (
                    <div>
                      <h4 className="font-medium mb-2">Additional Info</h4>
                      <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                        {selectedApp.additional_info}
                      </p>
                    </div>
                  )}

                  {/* Resume */}
                  {selectedApp.resume_filename && (
                    <div>
                      <h4 className="font-medium mb-2">Resume</h4>
                      <div className="flex items-center gap-2 text-sm bg-gray-50 p-3 rounded-lg">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span>{selectedApp.resume_filename}</span>
                        <Button variant="outline" size="sm" className="ml-auto" data-testid="download-resume">
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Notes
                    </h4>
                    
                    {selectedApp.notes?.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {selectedApp.notes.map((note, i) => (
                          <div key={i} className="text-sm bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400">
                            <p>{note.content}</p>
                            <p className="text-xs text-gray-400 mt-1">{formatDate(note.created_at)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Add a note..."
                        value={noteInput}
                        onChange={(e) => setNoteInput(e.target.value)}
                        rows={2}
                        data-testid="note-input"
                      />
                      <Button 
                        onClick={() => addNote(selectedApp._id)}
                        disabled={addingNote || !noteInput.trim()}
                        data-testid="add-note-btn"
                      >
                        {addingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
                      </Button>
                    </div>
                  </div>

                  {/* Timestamps */}
                  <div className="text-xs text-gray-400 pt-4 border-t">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Applied: {formatDate(selectedApp.created_at)}
                    </div>
                    {selectedApp.status_updated_at && (
                      <div className="flex items-center gap-1 mt-1">
                        <Clock className="h-3 w-3" />
                        Status updated: {formatDate(selectedApp.status_updated_at)}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="text-center py-16">
                <Eye className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Select an application to view details</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareersAdmin;
